import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Slide,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ChatBubbleOutlineTwoToneIcon from '@mui/icons-material/ChatBubbleOutlineTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import OpenInFullTwoToneIcon from '@mui/icons-material/OpenInFullTwoTone';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';

import useAuth from 'src/hooks/useAuth';
import { useDispatch, useSelector } from 'src/store';
import { registerActiveConversationSurface } from 'src/utils/notificationPresentation';
import {
  cleanupSmsListeners,
  initializeSmsListeners,
  requestMarkThreadRead,
  requestOptimizeMessageDraft,
  requestSendMessage,
  requestThread,
  requestThreads,
  setFilter,
  setSearchTerm
} from 'src/slices/sms';
import BottomBarContent from 'src/content/applications/Messenger/BottomBarContent';
import ChatContent from 'src/content/applications/Messenger/ChatContent';
import {
  archiveTeamGroup,
  cleanupTeamRealtime,
  createTeamGroup,
  initializeTeamRealtime,
  markTeamConversationRead,
  openTeamDirect,
  openTeamGroup,
  requestTeamDirectory,
  requestTeamMessages,
  sendTeamMessage,
  updateTeamGroup,
  uploadTeamAttachment
} from 'src/slices/teamChat';
import TeamConversation from './TeamConversation';
import TeamDirectory from './TeamDirectory';
import TeamGroupDialog from './TeamGroupDialog';

const LiveChatView = lazy(() => import('src/content/applications/Mailbox/Results/LiveChatView'));

const DRAWER_WIDTH = 376;

const getInitials = (name) => String(name || 'Customer')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const getCustomerName = (value) => {
  const fullName = [value?.first_name, value?.last_name].filter(Boolean).join(' ').trim();
  return value?.name || value?.customer_name || fullName || 'Customer';
};

const getCustomerPhone = (value) => value?.phone_number ||
  value?.customer_phone_number ||
  value?.phone ||
  value?.mobile_phone ||
  value?.customer_phone ||
  '';

const getThreadPreview = (thread) => {
  const message = String(thread?.message || thread?.last_message || '').trim();
  if (message) {
    return message;
  }

  const attachments = Array.isArray(thread?.attachments) ? thread.attachments : [];
  return attachments.length ? 'Attachment' : 'No message preview';
};

const getThreadTime = (thread) => {
  const rawValue = thread?.createdAt ||
    thread?.updated_at ||
    thread?.last_message_at ||
    thread?.created_at;
  const value = typeof rawValue?.toDate === 'function' ? rawValue.toDate() : rawValue;

  if (!value || !moment(value).isValid()) {
    return '';
  }

  return moment(value).fromNow(true);
};

const getUnreadCount = (thread) => Number(
  thread?.unread_count !== undefined
    ? thread.unread_count
    : (thread?.read === 0 ? 1 : 0)
) || 0;

function HeaderMessages() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuth();
  const searchTimerRef = useRef(null);

  const {
    messagesList,
    messagesData,
    customer,
    activeCustomerId,
    searchTerm,
    filter,
    unreadCount,
    listLoading,
    messageLoading,
    listError,
    threadError,
    loadingMoreThreads,
    loadingMoreMessages,
    threadsPagination,
    threadPagination,
    sending,
    sendError
  } = useSelector((state) => state.sms);
  const socketStatus = useSelector((state) => state.socket.connectionStatus);
  const teamChat = useSelector((state) => state.teamChat);
  const teamDirectoryRef = useRef(teamChat);
  teamDirectoryRef.current = teamChat;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(searchTerm || '');
  const [channel, setChannel] = useState('customers');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamConversationOpen, setTeamConversationOpen] = useState(false);
  const [liveChatSessionId, setLiveChatSessionId] = useState('');
  const [groupDialog, setGroupDialog] = useState(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  useEffect(() => registerActiveConversationSurface(
    'header-customer-conversation',
    'sms',
    activeCustomerId,
    conversationOpen
  ), [activeCustomerId, conversationOpen]);

  useEffect(() => registerActiveConversationSurface(
    'header-team-conversation',
    'team',
    teamChat.activeConversation?.id,
    teamConversationOpen
  ), [teamChat.activeConversation?.id, teamConversationOpen]);

  useEffect(() => {
    if (socketStatus !== 'connected' || !auth.isInitialized || !auth.isAuthenticated) {
      return undefined;
    }

    dispatch(initializeSmsListeners());
    const loadInitialThreads = () => dispatch(requestThreads({ reset: true }));
    const idleId = typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(loadInitialThreads, { timeout: 1500 })
      : null;
    const initialLoadTimer = idleId === null
      ? window.setTimeout(loadInitialThreads, 0)
      : null;

    return () => {
      if (idleId !== null) window.cancelIdleCallback(idleId);
      if (initialLoadTimer !== null) window.clearTimeout(initialLoadTimer);
      dispatch(cleanupSmsListeners());
    };
  }, [dispatch, socketStatus, auth.isInitialized, auth.isAuthenticated]);

  useEffect(() => {
    if (socketStatus !== 'connected' || !auth.isInitialized || !auth.isAuthenticated) {
      return undefined;
    }
    dispatch(initializeTeamRealtime());
    return () => dispatch(cleanupTeamRealtime());
  }, [dispatch, socketStatus, auth.isInitialized, auth.isAuthenticated, auth.user?.shop?.id]);

  useEffect(() => {
    setSearchInput(searchTerm || '');
  }, [searchTerm]);

  useEffect(() => {
    if (!drawerOpen || searchInput === searchTerm) {
      return undefined;
    }

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearchTerm(searchInput));
      dispatch(requestThreads({ reset: true, searchTerm: searchInput }));
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [dispatch, drawerOpen, searchInput, searchTerm]);

  useEffect(() => {
    if (conversationOpen && activeCustomerId && messagesData.length > 0) {
      dispatch(requestMarkThreadRead(activeCustomerId));
    }
  }, [dispatch, conversationOpen, activeCustomerId, messagesData.length]);

  const activeThread = useMemo(() => messagesList.find(
    (thread) => Number(thread.customer_id) === Number(activeCustomerId)
  ), [messagesList, activeCustomerId]);

  const activeCustomer = customer?.id && Number(customer.id) === Number(activeCustomerId)
    ? customer
    : activeThread;
  const activeCustomerLoaded = Boolean(
    customer?.id && Number(customer.id) === Number(activeCustomerId)
  );
  const activeCustomerName = getCustomerName(activeCustomer);
  const activeCustomerPhone = getCustomerPhone(activeCustomer);

  const openDrawer = () => {
    setDrawerOpen(true);
    if (channel === 'team') dispatch(requestTeamDirectory());
    else dispatch(requestThreads({ reset: true, background: messagesList.length > 0 }));
  };

  const selectThread = useCallback((customerId) => {
    const numericCustomerId = Number(customerId || 0);
    if (!numericCustomerId) {
      return;
    }

    setTeamConversationOpen(false);
    setLiveChatSessionId('');
    setChannel('customers');
    setConversationOpen(true);
    setDrawerOpen(false);
    dispatch(requestThread({ customerId: numericCustomerId, reset: true, markRead: true }));
    dispatch(requestMarkThreadRead(numericCustomerId));
  }, [dispatch]);

  useEffect(() => {
    const handleOpenCustomerThread = (event) => {
      const customerId = Number(event?.detail?.customerId || 0);
      if (customerId) {
        selectThread(customerId);
      }
    };

    window.addEventListener('autoclever:open-customer-message', handleOpenCustomerThread);
    return () => window.removeEventListener('autoclever:open-customer-message', handleOpenCustomerThread);
  }, [selectThread]);

  const selectEmployee = useCallback(async (employee) => {
    setConversationOpen(false);
    setLiveChatSessionId('');
    setTeamConversationOpen(true);
    setDrawerOpen(false);
    const result = await dispatch(openTeamDirect(employee));
    if (openTeamDirect.rejected.match(result)) setTeamConversationOpen(false);
  }, [dispatch]);

  const selectGroup = useCallback(async (group) => {
    setConversationOpen(false);
    setLiveChatSessionId('');
    setTeamConversationOpen(true);
    setDrawerOpen(false);
    await dispatch(openTeamGroup(group));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const handleOpenTeamMessage = async (event) => {
      const employeeId = String(event?.detail?.employeeId || '').trim();
      const conversationId = String(event?.detail?.conversationId || '').trim();
      if (!conversationId && !employeeId) return;
      setChannel('team');
      if (conversationId) {
        setConversationOpen(false);
        setLiveChatSessionId('');
        setDrawerOpen(false);
        setTeamConversationOpen(true);
        dispatch(requestTeamMessages({ conversation: {
          kind: 'direct', name: 'Team message', participants: [],
          ...event.detail.conversation, id: conversationId
        } }));
        return;
      }
      let employee = teamDirectoryRef.current.employees.find((item) => String(item.id) === employeeId);
      if (!employee) {
        const refreshed = await dispatch(requestTeamDirectory());
        if (requestTeamDirectory.fulfilled.match(refreshed)) {
          employee = (refreshed.payload.employees || []).find((item) => String(item.id) === employeeId);
        }
      }
      if (!cancelled) {
        if (employee) selectEmployee(employee);
        else setDrawerOpen(true);
      }
    };
    const handleOpenLiveChat = (event) => {
      const sessionId = String(event?.detail?.sessionId || '').trim();
      if (!sessionId) return;
      setConversationOpen(false);
      setTeamConversationOpen(false);
      setDrawerOpen(false);
      setLiveChatSessionId(sessionId);
    };
    window.addEventListener('autoclever:open-team-message', handleOpenTeamMessage);
    window.addEventListener('autoclever:open-live-chat', handleOpenLiveChat);
    return () => {
      cancelled = true;
      window.removeEventListener('autoclever:open-team-message', handleOpenTeamMessage);
      window.removeEventListener('autoclever:open-live-chat', handleOpenLiveChat);
    };
  }, [dispatch, selectEmployee]);

  useEffect(() => {
    if (teamConversationOpen && teamChat.activeConversation?.id && teamChat.messages.length) {
      dispatch(markTeamConversationRead(teamChat.activeConversation.id));
    }
  }, [dispatch, teamConversationOpen, teamChat.activeConversation?.id, teamChat.messages.length]);

  const changeChannel = (_, value) => {
    setChannel(value);
    if (value === 'team') dispatch(requestTeamDirectory());
    else dispatch(requestThreads({ reset: true, background: messagesList.length > 0 }));
  };

  const saveGroup = async (input) => {
    const action = groupDialog?.id
      ? updateTeamGroup({ conversationId: groupDialog.id, ...input })
      : createTeamGroup(input);
    const result = await dispatch(action);
    const matcher = groupDialog?.id ? updateTeamGroup.fulfilled : createTeamGroup.fulfilled;
    if (matcher.match(result)) {
      setGroupDialog(null);
      if (!groupDialog?.id && result.payload) selectGroup(result.payload);
    }
  };

  const sendTeam = async (payload) => {
    const result = await dispatch(sendTeamMessage({
      conversationId: teamChat.activeConversation.id,
      ...payload
    }));
    return sendTeamMessage.fulfilled.match(result);
  };

  const archiveActiveGroup = async () => {
    if (!teamChat.activeConversation?.id) return;
    const result = await dispatch(archiveTeamGroup(teamChat.activeConversation.id));
    if (archiveTeamGroup.fulfilled.match(result)) {
      setArchiveConfirmOpen(false);
      setTeamConversationOpen(false);
    }
  };

  const changeFilter = (_, value) => {
    if (!value) {
      return;
    }

    dispatch(setFilter(value));
    dispatch(requestThreads({ reset: true, filter: value }));
  };

  const loadMoreThreads = () => {
    if (threadsPagination.hasMore && !loadingMoreThreads) {
      dispatch(requestThreads({ append: true }));
    }
  };

  const loadOlderMessages = () => {
    if (activeCustomerId && threadPagination.hasMore && !loadingMoreMessages) {
      dispatch(requestThread({
        customerId: activeCustomerId,
        append: true,
        markRead: false
      }));
    }
  };

  const openFullMessenger = () => {
    const query = activeCustomerId ? `?customerId=${activeCustomerId}` : '';
    setConversationOpen(false);
    setDrawerOpen(false);
    navigate(`/app/applications/messenger${query}`);
  };

  const openFullTeamMessenger = () => {
    setTeamConversationOpen(false);
    setDrawerOpen(false);
    navigate('/app/applications/team-messenger');
  };

  const optimizeDraft = async (message) => dispatch(
    requestOptimizeMessageDraft(message)
  ).unwrap();

  const totalUnread = Number(unreadCount || 0) + Number(teamChat.unreadCount || 0);

  return (
    <>
      <Tooltip arrow title="Messages">
        <Button
          color="secondary"
          aria-label={`Messages${totalUnread ? `, ${totalUnread} unread` : ''}`}
          onClick={openDrawer}
          sx={{
            minWidth: 40,
            height: 40,
            px: { xs: 1, lg: 1.25 },
            gap: 0.75,
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 700
          }}
        >
          <Badge badgeContent={totalUnread} color="error" max={99}>
            <ChatBubbleOutlineTwoToneIcon fontSize="small" />
          </Badge>
          <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
            Message
          </Box>
        </Button>
      </Tooltip>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true, disableEnforceFocus: true }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box minWidth={0}>
              <Typography variant="h4">Messages</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {channel === 'team' ? 'Employees and team groups' : 'Customer conversations'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Tooltip title="Refresh conversations">
                <IconButton
                  size="small"
                  onClick={() => channel === 'team'
                    ? dispatch(requestTeamDirectory())
                    : dispatch(requestThreads({ reset: true, background: true }))}
                >
                  <RefreshTwoToneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close messages">
                <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                  <CloseTwoToneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Tabs
            value={channel}
            onChange={changeChannel}
            variant="fullWidth"
            sx={{ mt: 1.25, minHeight: 38 }}
          >
            <Tab value="customers" label="Customers" sx={{ minHeight: 38 }} />
            <Tab
              value="team"
              label={<Badge color="error" badgeContent={teamChat.unreadCount || 0} invisible={!teamChat.unreadCount}>Team</Badge>}
              sx={{ minHeight: 38 }}
            />
          </Tabs>

          <TextField
            fullWidth
            size="small"
            value={channel === 'team' ? teamSearch : searchInput}
            placeholder={channel === 'team' ? 'Search employees or groups' : 'Search customers or messages'}
            onChange={(event) => channel === 'team'
              ? setTeamSearch(event.target.value)
              : setSearchInput(event.target.value)}
            sx={{ mt: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          {channel === 'customers' ? (
            <Tabs
              value={filter}
              onChange={changeFilter}
              variant="fullWidth"
              sx={{ mt: 0.75, minHeight: 38 }}
            >
              <Tab value="all" label="All" sx={{ minHeight: 38 }} />
              <Tab value="unread" label="Unread" sx={{ minHeight: 38 }} />
            </Tabs>
          ) : null}
        </Box>

        <Divider />

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 0.75 }}>
          {channel === 'team' ? (
            <TeamDirectory
              employees={teamChat.employees}
              groups={teamChat.groups}
              viewer={teamChat.viewer}
              loading={teamChat.directoryLoading}
              error={teamChat.directoryError}
              search={teamSearch}
              activeConversationId={teamChat.activeConversation?.id}
              onSelectEmployee={selectEmployee}
              onSelectGroup={selectGroup}
              onCreateGroup={() => setGroupDialog({})}
              onRetry={() => dispatch(requestTeamDirectory())}
            />
          ) : (
            <>
          {listLoading && messagesList.length === 0 && (
            <Box py={5} display="flex" justifyContent="center">
              <CircularProgress size={28} />
            </Box>
          )}

          {!listLoading && listError && messagesList.length === 0 && (
            <Box px={2} py={4} textAlign="center">
              <Typography variant="body2" color="error.main">
                {listError}
              </Typography>
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => dispatch(requestThreads({ reset: true }))}
              >
                Retry
              </Button>
            </Box>
          )}

          {!listLoading && !listError && messagesList.length === 0 && (
            <Box px={2} py={5} textAlign="center">
              <Typography variant="h5">No conversations found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Customer SMS threads will appear here.
              </Typography>
            </Box>
          )}

          <List disablePadding>
            {messagesList.map((thread) => {
              const threadUnreadCount = getUnreadCount(thread);
              const selected = Number(activeCustomerId) === Number(thread.customer_id);
              const customerName = getCustomerName(thread);

              return (
                <ListItemButton
                  key={thread.customer_id}
                  selected={selected && conversationOpen}
                  onClick={() => selectThread(thread.customer_id)}
                  sx={{
                    alignItems: 'flex-start',
                    borderRadius: 1,
                    px: 1.25,
                    py: 1,
                    mb: 0.25,
                    '&.Mui-selected': {
                      backgroundColor: theme.colors.primary.lighter
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Badge
                      color="error"
                      badgeContent={threadUnreadCount}
                      max={99}
                      invisible={!threadUnreadCount}
                    >
                      <Avatar sx={{ width: 36, height: 36, fontSize: 13 }}>
                        {getInitials(customerName)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={(
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: threadUnreadCount ? 700 : 600 }}
                        >
                          {customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {getThreadTime(thread)}
                        </Typography>
                      </Box>
                    )}
                    secondary={(
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block', mt: 0.25, fontWeight: threadUnreadCount ? 600 : 400 }}
                      >
                        {getThreadPreview(thread)}
                      </Typography>
                    )}
                  />
                </ListItemButton>
              );
            })}
          </List>

          {threadsPagination.hasMore && (
            <Box p={1}>
              <Button fullWidth size="small" disabled={loadingMoreThreads} onClick={loadMoreThreads}>
                {loadingMoreThreads ? 'Loading...' : 'Load more'}
              </Button>
            </Box>
          )}
            </>
          )}
        </Box>
      </Drawer>

      <Slide direction="up" in={conversationOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={12}
          role="dialog"
          aria-label={`Conversation with ${activeCustomerName}`}
          sx={{
            position: 'fixed',
            zIndex: theme.zIndex.drawer + 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: isMobile ? '100%' : 410,
            maxWidth: '100vw',
            height: isMobile ? `calc(100vh - ${theme.header.height})` : 560,
            maxHeight: isMobile ? undefined : `calc(100vh - ${theme.header.height} - 24px)`,
            right: isMobile ? 0 : (drawerOpen ? DRAWER_WIDTH + 14 : 16),
            bottom: 0,
            top: isMobile ? theme.header.height : 'auto',
            borderRadius: isMobile ? 0 : `${theme.general.borderRadius} ${theme.general.borderRadius} 0 0`,
            border: `1px solid ${theme.colors.alpha.black[20]}`,
            transition: theme.transitions.create('right', {
              duration: theme.transitions.duration.shorter
            })
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              borderBottom: `1px solid ${theme.colors.alpha.black[10]}`,
              backgroundColor: theme.colors.alpha.white[100]
            }}
          >
            <Avatar sx={{ width: 34, height: 34, mr: 1, fontSize: 12 }}>
              {getInitials(activeCustomerName)}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h5" noWrap>{activeCustomerName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {activeCustomerPhone || 'SMS conversation'}
              </Typography>
            </Box>
            <Tooltip title="Open Messenger">
              <IconButton size="small" onClick={openFullMessenger}>
                <OpenInFullTwoToneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close conversation">
              <IconButton size="small" onClick={() => setConversationOpen(false)}>
                <CloseTwoToneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box flex={1} minHeight={0} bgcolor={theme.colors.alpha.black[5]}>
            <ChatContent
              compact
              customer={activeCustomer}
              data={messagesData}
              loading={messageLoading}
              error={threadError}
              hasMore={threadPagination.hasMore}
              loadingMore={loadingMoreMessages}
              onLoadMore={loadOlderMessages}
              onRetry={() => activeCustomerId && dispatch(requestThread({
                customerId: activeCustomerId,
                reset: true,
                markRead: true
              }))}
            />
          </Box>

          <Divider />
          <BottomBarContent
            compact
            disabled={!activeCustomerId || !activeCustomerLoaded || messageLoading}
            sending={sending}
            sendError={sendError}
            onSend={(payload) => dispatch(requestSendMessage(payload))}
            onOptimizeDraft={optimizeDraft}
          />
        </Paper>
      </Slide>

      {liveChatSessionId && (
        <Paper
          elevation={12}
          role="dialog"
          aria-label="Website chat conversation"
          sx={{
            position: 'fixed', zIndex: theme.zIndex.drawer + 2,
            right: { xs: 0, md: 16 }, bottom: 0,
            width: { xs: '100%', md: 440 }, maxWidth: '100vw',
            maxHeight: `calc(100dvh - ${theme.header.height} - 12px)`,
            overflowY: 'auto', borderRadius: '12px 12px 0 0'
          }}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h5" sx={{ flex: 1 }}>Website chat</Typography>
            <IconButton aria-label="Open full website chat" onClick={() => {
              navigate(`/app/applications/dealership/livechat?sessionId=${encodeURIComponent(liveChatSessionId)}`);
              setLiveChatSessionId('');
            }}><OpenInFullTwoToneIcon fontSize="small" /></IconButton>
            <IconButton aria-label="Close website chat" onClick={() => setLiveChatSessionId('')}>
              <CloseTwoToneIcon fontSize="small" />
            </IconButton>
          </Box>
          <Suspense fallback={<Box p={4} textAlign="center"><CircularProgress size={24} /></Box>}>
            <LiveChatView key={liveChatSessionId} sessionId={liveChatSessionId} compact />
          </Suspense>
        </Paper>
      )}

      <TeamConversation
        open={teamConversationOpen && Boolean(teamChat.activeConversation)}
        drawerOpen={drawerOpen}
        conversation={teamChat.activeConversation}
        viewer={teamChat.viewer}
        messages={teamChat.messages}
        loading={teamChat.messagesLoading}
        error={teamChat.messagesError}
        sending={teamChat.sending}
        sendError={teamChat.sendError}
        hasMore={teamChat.hasMore}
        loadingMore={teamChat.loadingMore}
        typingByActor={teamChat.typingByActor}
        onClose={() => setTeamConversationOpen(false)}
        onOpenFull={openFullTeamMessenger}
        onLoadMore={() => dispatch(requestTeamMessages({ conversation: teamChat.activeConversation, append: true }))}
        onRetry={() => dispatch(requestTeamMessages({ conversation: teamChat.activeConversation }))}
        onSend={sendTeam}
        onUpload={(file) => dispatch(uploadTeamAttachment(file)).unwrap()}
        onManageGroup={() => setGroupDialog(teamChat.activeConversation)}
        onArchiveGroup={() => setArchiveConfirmOpen(true)}
      />

      <Dialog open={archiveConfirmOpen} onClose={() => setArchiveConfirmOpen(false)}>
        <DialogTitle>Archive team group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The group will leave the Team directory, but its message history will remain stored.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={archiveActiveGroup}>Archive</Button>
        </DialogActions>
      </Dialog>

      <TeamGroupDialog
        open={Boolean(groupDialog)}
        employees={teamChat.employees}
        group={groupDialog?.id ? groupDialog : null}
        saving={teamChat.groupSaving}
        error={teamChat.groupError}
        onClose={() => setGroupDialog(null)}
        onSave={saveGroup}
      />
    </>
  );
}

export default HeaderMessages;
